import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, Loader2, Film, Image as ImageIcon } from 'lucide-react';
import { videoStudioService } from '../../services/videoStudioService';
import { imageStudioService } from '../../services/imageStudioService';
import FolderSidebar from '../media/FolderSidebar';

export type GalleryAsset = {
  id: string;
  title: string;
  url: string;
  thumbnail_url?: string;
  mime_type: string;
  duration_seconds?: number;
  width?: number;
  height?: number;
};

interface Props {
  open: boolean;
  source: 'video' | 'image' | null;
  onClose: () => void;
  onPick: (asset: GalleryAsset) => void;
}

const GalleryPicker: React.FC<Props> = ({ open, source, onClose, onPick }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<GalleryAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // null = todas; 'none' = sem pasta; UUID = pasta específica
  const [selectedFolderId, setSelectedFolderId] = useState<string | null | 'none'>(null);

  // Reset folder selection ao reabrir
  useEffect(() => {
    if (open) setSelectedFolderId(null);
  }, [open]);

  useEffect(() => {
    if (!open || !source) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const folderFilter = selectedFolderId !== null ? { folder_id: selectedFolderId as any } : {};
        if (source === 'video') {
          const r = await videoStudioService.listVideos({ limit: 100, ...folderFilter });
          if (!cancel && r.success) {
            const mapped: GalleryAsset[] = (r.data || [])
              .filter(v => v.original_url && (v.mime_type || '').startsWith('video/'))
              .map(v => ({
                id: v.id,
                title: v.title || 'Vídeo',
                url: v.original_url!,
                thumbnail_url: v.thumbnail_url || undefined,
                mime_type: v.mime_type || 'video/mp4',
                duration_seconds: v.duration_seconds || undefined,
                width: v.width || undefined,
                height: v.height || undefined
              }));
            setItems(mapped);
          }
        } else {
          const r = await imageStudioService.getImages({ limit: 100, ...folderFilter });
          if (!cancel && r.success) {
            const mapped: GalleryAsset[] = (r.data || [])
              .filter((img: any) => img.original_url || img.thumbnail_url)
              .map((img: any) => ({
                id: img.id,
                title: img.title || (img.prompt ? img.prompt.slice(0, 60) : 'Imagem'),
                url: img.original_url || img.thumbnail_url,
                thumbnail_url: img.thumbnail_url || img.original_url,
                mime_type: img.mime_type || 'image/png',
                width: img.width || undefined,
                height: img.height || undefined
              }));
            setItems(mapped);
          }
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, source, selectedFolderId]);

  if (!open || !source) return null;

  const filtered = search
    ? items.filter(it => it.title.toLowerCase().includes(search.toLowerCase()))
    : items;

  const isVideo = source === 'video';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[85vh] flex flex-col bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-base font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
            {isVideo ? <Film className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
            {isVideo
              ? t('videoStudio.gallery.pickerTitleVideo', 'Galeria de vídeos')
              : t('videoStudio.gallery.pickerTitleImage', 'Galeria de imagens')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={isVideo
                ? (t('videoStudio.gallery.searchVideoPlaceholder', 'Buscar vídeo pelo nome…') as string)
                : (t('videoStudio.gallery.searchImagePlaceholder', 'Buscar imagem pelo nome…') as string)}
              className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-xs focus:outline-none focus:border-primary-500/50"
            />
          </div>
        </div>

        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Sidebar de pastas compact */}
          <aside className="w-48 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 p-2 overflow-y-auto">
            <FolderSidebar
              mediaType={source === 'video' ? 'video' : 'image'}
              selectedFolderId={selectedFolderId}
              onSelect={setSelectedFolderId}
              compact
            />
          </aside>
          <div className="flex-1 overflow-y-auto p-3 min-w-0">
          {loading ? (
            <div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 text-gray-500 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <p className="p-12 text-center text-sm text-gray-500">
              {items.length === 0
                ? (isVideo
                    ? t('videoStudio.gallery.emptyVideo', 'Nenhum vídeo disponível ainda.')
                    : t('videoStudio.gallery.emptyImage', 'Nenhuma imagem disponível ainda.'))
                : t('videoStudio.gallery.noMatches', 'Nada encontrado com esse termo.')}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map(it => (
                <button key={it.id}
                  onClick={() => { onPick(it); onClose(); }}
                  className="group rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800 hover:border-primary-500/60 transition bg-white dark:bg-gray-800 flex flex-col text-left"
                  title={it.title}
                >
                  <div className="aspect-video bg-black flex items-center justify-center overflow-hidden">
                    {isVideo ? (
                      it.thumbnail_url
                        ? <img src={it.thumbnail_url} alt="" className="max-w-full max-h-full object-contain" />
                        : <video src={it.url} muted preload="metadata" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <img src={it.url} alt="" className="max-w-full max-h-full object-contain" />
                    )}
                  </div>
                  <div className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-[11px] font-medium text-gray-900 dark:text-white truncate">{it.title}</p>
                    <p className="text-[10px] text-gray-500 truncate">
                      {it.duration_seconds ? `${Math.round(it.duration_seconds)}s · ` : ''}
                      {it.width && it.height ? `${it.width}×${it.height}` : it.mime_type}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
          </div>
        </div>

        <div className="p-3 border-t border-gray-200 dark:border-gray-800 text-[11px] text-gray-500 text-center">
          {t('videoStudio.gallery.footerCount', { count: filtered.length, defaultValue: '{{count}} itens · Clique pra adicionar' })}
        </div>
      </div>
    </div>
  );
};

export default GalleryPicker;
